import clsx from 'clsx';
import _ from 'lodash';
import React from 'react';
import { Field, useForm } from 'react-final-form';
import { Checkbox } from '@/core/Checkbox.atom';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { canUseMemo, getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { ALL } from '@/tools/periodicCondition/periodicCondition.constants';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export interface CheckboxTableIF extends ValidatingFormComponent<string | string[] | undefined> {
  component: 'CheckboxTableFormComponent';
  options: { key: string; display: string }[];
  includeAll?: boolean;
  type?: 'radio' | 'checkbox';
}

export const CheckboxTableFormComponentUnwrapped = (props: CheckboxTableIF) => {
  const {
    name,
    extraClassNames,
    onChange,
    options,
    includeAll = false,
    validation,
    extendValidation,
    value, // value is the array of the keys of the selected options
    type = 'checkbox',
    required,
    testId,
    customErrorText,
  } = props;

  const tableOptions = includeAll ? _.concat(ALL, options) : options;

  const isOptionSelected = (key: string) =>
    type === 'radio' ? key === value : (key === 'all' && includeAll && _.isEmpty(value)) || _.includes(value, key);

  const defaultValidation = (value: string | string[] | undefined) =>
    type === 'radio'
      ? _.isEmpty(value)
      : _.isEmpty(value) && !isOptionSelected('all') && Boolean(includeAll || required);

  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);
  const formState = useForm().getState();

  return (
    <FormFieldWrapper
      id={`${name}-field-wrapper`}
      testId={testId}
      wrapperClassNames={clsx('flexFill', extraClassNames)}
      showError={_.isEmpty(value)}
      customErrorText={customErrorText}>
      <Field<string | string[]> name={name} validate={appliedValidation}>
        {({ input, meta }) => (
          <table
            className={clsx('table', 'table-striped', 'table-condensed', 'tw:mb-0', extraClassNames)}
            id={name}
            data-testid={name}
            tabIndex={0}>
            <tbody>
              {_.map(tableOptions, (option) => (
                <tr key={option.key} className="cursorPointer">
                  <td
                    className="tw:p-0!"
                    onClick={(e) => {
                      e.preventDefault();
                      onChange?.(option.key);
                    }}>
                    <Checkbox
                      {..._.omit(getFormFieldProps(formState, input, meta, props), 'onChange')}
                      isChecked={isOptionSelected(option.key)}
                      id={`${option.key}Checkbox`}
                      label={option.display}
                      type={type}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Field>
    </FormFieldWrapper>
  );
};

export const CheckboxTableFormComponent = React.memo(CheckboxTableFormComponentUnwrapped, canUseMemo);
